import styled from "../_snowpack/pkg/@emotion/styled.js";
import React, {useRef, useState} from "../_snowpack/pkg/react.js";
import request from "../common/request.js";
import {colors, fontFamily, media} from "./theme.js";
import {XCircle, BigPlus} from "./icons.js";
import Stack from "./stack.js";
import {t} from "../common/i18n.js";
import Loader from "./loader.js";
import {withAttrs} from "../common/utils.js";
import {alert} from "./confirm.js";
import {getUrl} from "../common/universal.js";
import {Routes} from "../typings/routes.js";
import {printSurfaceLabel} from "../common/translations.js";
import {captureException} from "../_snowpack/pkg/@sentry/react.js";
const maxSize = 300 * 1024 * 1024;
export default function UploadArea(props) {
  const {file, type, acceptMimeTypes = [], onChange, ...rest} = props;
  const accept = acceptMimeTypes.join(",");
  const inputRef = useRef(null);
  const [upload, setUpload] = useState(file);
  const [dragging, setDrapping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const uploadAndSet = async (file2) => {
    try {
      setLoading(true);
      setProgress(0);
      const uploaded = await uploadFile(file2, setProgress);
      const uplaodedFile = {...uploaded, type: file2.type};
      setUpload(uplaodedFile);
      onChange && onChange(uplaodedFile);
    } catch (e) {
      captureException(e);
      setLoading(false);
      if (e.code === 1009) {
        return alert({title: t("Datei-Fehler"), message: e.message});
      }
      return alert({message: t("Bitte versuche es später nochmal!")});
    } finally {
      setProgress(0);
    }
  };
  const onInputChange = async (e) => {
    const file2 = e.target.files[0];
    if (!checkFileType(file2)) {
      return alert({
        message: t("Dateityp wird nicht unterstützt. Bitte eine PDF-Datei hochladen.")
      });
    }
    if (file2.size === 0) {
      return alert({
        title: t("Diese Datei ist leer!"),
        message: t("Die hochgeladene Datei ist leer. Bitte versuche es mit einer anderen Datei.")
      });
    }
    if (file2.size > maxSize) {
      return alert({
        title: t("Diese Datei ist leider zu groß!"),
        message: t("Die hochgeladene Datei überschreitet das Limit von maximal 300 MB. Bitte versuche es mit einer kleineren Datei.")
      });
    }
    await uploadAndSet(file2);
  };
  const onCloseClick = () => {
    setUpload(void 0);
    onChange && onChange();
  };
  const onAreaClick = () => {
    if (!inputRef.current)
      return;
    inputRef.current.click();
  };
  const onDrop = async (e) => {
    e.preventDefault();
    const {files} = e.dataTransfer;
    await uploadAndSet(files[0]);
    setDrapping(false);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDrapping(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDrapping(false);
  };
  const checkFileType = (file2) => {
    if (acceptMimeTypes.length === 0)
      return true;
    return acceptMimeTypes.includes(file2.type);
  };
  const onImageLoad = () => {
    setLoading(false);
  };
  const onImageError = () => {
    setLoading(false);
    setUpload(void 0);
    onChange && onChange();
    alert({
      title: t("Datei-Fehler"),
      message: t("Die Datei konnte leider nicht verarbeitet werden. Bitte versuche es mit einer anderen Datei aus.")
    });
  };
  const hasUpload = upload !== void 0;
  return /* @__PURE__ */ React.createElement(Container, {
    dragging,
    ...rest
  }, hasUpload ? /* @__PURE__ */ React.createElement(Inner, {
    loading
  }, /* @__PURE__ */ React.createElement(PreviewImageContainer, null, /* @__PURE__ */ React.createElement(PreviewImage, {
    src: upload.thumbnailUrl,
    onLoad: onImageLoad,
    onError: onImageError,
    loading: "lazy"
  }), /* @__PURE__ */ React.createElement(Close, {
    onClick: onCloseClick
  })), /* @__PURE__ */ React.createElement(FileName, null, upload.name)) : !loading && /* @__PURE__ */ React.createElement(Inner, null, /* @__PURE__ */ React.createElement(FileInput, {
    ref: inputRef,
    type: "file",
    accept,
    onChange: onInputChange
  }), /* @__PURE__ */ React.createElement(ClickAndDragArea, {
    onClick: onAreaClick,
    onDrop,
    onDragOver,
    onDragLeave,
    title: t("Klicke hier oder ziehe deine Druckvorlage in diesen Bereich")
  }, /* @__PURE__ */ React.createElement(Placeholder, null, /* @__PURE__ */ React.createElement(BigPlus, null), /* @__PURE__ */ React.createElement(PlaceholderTitle, null, t("Druckvorlage"), " ", printSurfaceLabel(type), " ", t("hochladen"))))), loading && /* @__PURE__ */ React.createElement(LoaderContainer, null, /* @__PURE__ */ React.createElement(Loader, null), progress > 0 && /* @__PURE__ */ React.createElement(Progress, null, Math.floor(progress), "% ", t("hochgeladen")), hasUpload && /* @__PURE__ */ React.createElement(Progress, null, t("Textur wird generiert"))));
}
async function uploadFile(file, fn) {
  const presignedUpload = getUrl(Routes.presignedUploadPrintTemple);
  const {uploadUrl, formData, ...rest} = await request(presignedUpload);
  const form = new FormData();
  Object.entries(formData).forEach(([k, v]) => form.append(k, v));
  form.append("file", file, file.name);
  const req = new XMLHttpRequest();
  req.open("POST", uploadUrl);
  const promise = new Promise((resolve, reject) => {
    req.addEventListener("error", reject);
    req.addEventListener("load", () => {
      if (req.status >= 300) {
        return reject(req);
      }
      return resolve({...rest, name: file.name});
    });
  });
  req.upload.addEventListener("progress", function(e) {
    fn(e.loaded / e.total * 100);
  });
  req.send(form);
  return promise;
}
const Container = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  background: ${colors.c3};
  border-radius: 4px;
  border: 2px solid ${colors.c};
  ${({dragging}) => dragging && `border: 2px dashed ${colors.c};`}

  ${media.mobile} {
    width: 100px;
    height: 100px;
  }
`;
const Inner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  visibility: visible;

  ${({loading}) => loading && `visibility: hidden;`}
`;
const FileInput = styled.input`
  display: none;
`;
const ClickAndDragArea = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  cursor: pointer;
`;
const Placeholder = styled(withAttrs(Stack, {spacing: "s"}))`
  text-align: center;
  align-items: center;
  padding: 7px;

  ${media.mobile} {
    --spacing: 0px;
  }
`;
const PlaceholderTitle = styled.div`
  font-family: ${fontFamily.primary};
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  color: ${colors.c};
`;
const PreviewImageContainer = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  bottom: 32px;
  background: ${colors.bg};
`;
const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const Close = styled(XCircle)`
  position: absolute;
  top: -12px;
  right: -12px;
  cursor: pointer;
`;
const FileName = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 5px;
  height: 22px;
  font-family: ${fontFamily.primary};
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${colors.b2};
`;
const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  text-align: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px;

  & > svg {
    width: 130px;
    height: 130px;
    position: absolute;
    top: calc(50% - 65px);
    left: calc(50% - 65px);

    ${media.mobile} {
      width: 80px;
      height: 80px;
      top: calc(50% - 40px);
      left: calc(50% - 40px);
    }
  }
`;
const Progress = styled.div`
  font-family: ${fontFamily.primary};
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  color: ${colors.c};
`;
